// Set by TutorialContext on every scrollable element while a tour runs; its CSS (see
// `.tutorial-scroll-lock` in Layout.scss) makes the element `overflow: hidden !important`,
// so computed style alone no longer shows that it scrolls.
export const SCROLL_LOCK_CLASS = 'tutorial-scroll-lock';

/**
 * driver.js decides whether to scroll a step's target by comparing it with the
 * *window* viewport. The app never scrolls the window: pages scroll inside
 * `.layout-content`, which starts below the top header, and panels or table wrappers
 * can scroll inside that. A target scrolled out of any of those scrollers but still
 * inside the window is left where it is, and the highlight lands on whatever is
 * there (the header, empty space) instead of the target.
 *
 * This walks the target's scrollable ancestors, innermost first, and scrolls each one
 * just enough to bring the target into its visible area, vertically and horizontally,
 * so driver.js's own positioning then finds it where it expects. It moves only the
 * scrollers themselves, never `overflow: hidden` ancestors such as the app shell, which
 * `scrollIntoView` would also shift. Scrollers are found from the target, not from
 * what a tour has locked so far, so it works on the first step too.
 */
export function ensureTargetVisible(element: Element): void {
  // No client rects means the target is not rendered at all (display: none, detached).
  if (element.getClientRects().length === 0) return;

  for (let node = element.parentElement; node && node !== document.documentElement; node = node.parentElement) {
    if (!isScroller(node)) continue;

    const view = node.getBoundingClientRect();
    if (!view.width || !view.height) continue;
    // Measured per scroller: scrolling an inner one moves the target relative to the rest.
    const target = element.getBoundingClientRect();
    const dy = verticalDelta(target, view);
    const dx = horizontalDelta(target, view);
    if (dy) node.scrollTop += dy;
    if (dx) node.scrollLeft += dx;
  }
}

function isScroller(node: HTMLElement): boolean {
  if (node.classList.contains(SCROLL_LOCK_CLASS)) return true;
  const { overflowX, overflowY } = window.getComputedStyle(node);
  return [overflowX, overflowY].some((value) => value === 'auto' || value === 'scroll');
}

// A target taller than the visible area can't be centred; align its top instead so its
// heading is what the popover points at. Otherwise centre it, which leaves room for the
// popover on either side.
function verticalDelta(target: DOMRect, view: DOMRect): number {
  if (target.top >= view.top && target.bottom <= view.bottom) return 0;
  if (target.height > view.height) return target.top - view.top;
  return (target.top + target.bottom) / 2 - (view.top + view.bottom) / 2;
}

// Sideways, move only as far as needed to reveal the target.
function horizontalDelta(target: DOMRect, view: DOMRect): number {
  if (target.left >= view.left && target.right <= view.right) return 0;
  if (target.width > view.width || target.left < view.left) return target.left - view.left;
  return target.right - view.right;
}
